import { ProjectCallsPatternType } from "@prisma/client";
import IconChatBubble from "@/app/(components)/generics/Icons/IconChatBubble";
import IconDangerTriangle from "@/app/(components)/generics/Icons/IconDangerTriangle";
import IconFlag from "@/app/(components)/generics/Icons/IconFlag";
import IconTrendingUp from "@/app/(components)/generics/Icons/IconTrendingUp";
import IconUser from "@/app/(components)/generics/Icons/IconUser";
import IconUsers from "@/app/(components)/generics/Icons/IconUsers";
import PointsLoader from "@/app/(components)/generics/loaders/PointsLoader/PointsLoader";
import { useAuthUser } from "@/app/(components)/specifics/wrappers/AuthProvider/AuthProvider";
import { useFetchProjectPatterns } from "@/app/hooks/fetchs/project/useFetchProjectPatterns";
import { ProjectStore } from "@/app/stores/ProjectStore";
import { TKey } from "@/app/translations/TKey";
import { ProjectCallsRecommendationKind } from "@/app/types/call/CallAnalysis";
import { CallScopeFilter } from "@/app/types/call/CallScopeFilter";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import { InsightCallSource } from "@/app/types/project/InsightCallSource";
import { UCallScope } from "@/app/utils/UCallScope";
import { Limits } from "@/app/vars/Limits";
import styles from "./Analysis.module.css";
import AnalysisCategory from "./components/AnalysisCategory/AnalysisCategory";
import AnalysisRecommendations from "./components/AnalysisRecommendations/AnalysisRecommendations";

type Props = DefaultProps & {
  filter?: CallScopeFilter;
  callsLoading?: boolean;
};

export default function AnalysisDynamics(props: Props) {
  const { params, filter = {}, callsLoading = false } = props;
  const user = useAuthUser();
  const project = ProjectStore((state) => state.currentProject);
  const { data: patterns, isLoading } = useFetchProjectPatterns(project?.id, filter);
  const _lang = params.lang;

  if (isLoading || callsLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "64px 0" }}>
        <PointsLoader
          centered
          color="var(--color-green-3)"
          delay={Limits.CALL_ANALYSIS_LOADER_DELAY}
        />
      </div>
    );
  }

  const hasAnyPatterns = patterns && Object.values(patterns).some((cat) => cat.totalCount > 0);

  if (user && (!project || !hasAnyPatterns)) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.leftColumn}>
        <AnalysisCategory
          params={params}
          source={InsightCallSource.PATTERN}
          filter={filter}
          titleKey={TKey.CALL_ANALYSIS_PATTERN_TALK_BALANCE}
          subtitleKey={TKey.CALL_ANALYSIS_PATTERN_TALK_BALANCE_SUB}
          patternType={ProjectCallsPatternType.TALK_BALANCE}
          data={patterns?.TALK_BALANCE}
          icon={<IconUsers />}
          iconSize={20}
          colors={{ color: "var(--color-pattern-balance)", bg: "var(--color-pattern-balance-bg)" }}
        />
        <AnalysisCategory
          params={params}
          source={InsightCallSource.PATTERN}
          filter={filter}
          titleKey={TKey.CALL_ANALYSIS_PATTERN_PROSPECT_ENGAGEMENT}
          subtitleKey={TKey.CALL_ANALYSIS_PATTERN_PROSPECT_ENGAGEMENT_SUB}
          patternType={ProjectCallsPatternType.PROSPECT_ENGAGEMENT}
          data={patterns?.PROSPECT_ENGAGEMENT}
          icon={<IconUser />}
          iconSize={20}
          colors={{
            color: "var(--color-pattern-engagement)",
            bg: "var(--color-pattern-engagement-bg)",
          }}
        />
        <AnalysisCategory
          params={params}
          source={InsightCallSource.PATTERN}
          filter={filter}
          titleKey={TKey.CALL_ANALYSIS_PATTERN_SPEECH_CLARITY}
          subtitleKey={TKey.CALL_ANALYSIS_PATTERN_SPEECH_CLARITY_SUB}
          patternType={ProjectCallsPatternType.SPEECH_CLARITY}
          data={patterns?.SPEECH_CLARITY}
          icon={<IconChatBubble />}
          iconSize={20}
          colors={{ color: "var(--color-pattern-speech)", bg: "var(--color-pattern-speech-bg)" }}
        />
        <AnalysisCategory
          params={params}
          source={InsightCallSource.PATTERN}
          filter={filter}
          titleKey={TKey.CALL_ANALYSIS_PATTERN_NEXT_STEPS}
          subtitleKey={TKey.CALL_ANALYSIS_PATTERN_NEXT_STEPS_SUB}
          patternType={ProjectCallsPatternType.NEXT_STEPS}
          data={patterns?.NEXT_STEPS}
          icon={<IconFlag />}
          iconSize={20}
          colors={{
            color: "var(--color-pattern-next-steps)",
            bg: "var(--color-pattern-next-steps-bg)",
          }}
        />
        <AnalysisCategory
          params={params}
          source={InsightCallSource.PATTERN}
          filter={filter}
          titleKey={TKey.CALL_ANALYSIS_PATTERN_FRICTION_MOMENTS}
          subtitleKey={TKey.CALL_ANALYSIS_PATTERN_FRICTION_MOMENTS_SUB}
          patternType={ProjectCallsPatternType.FRICTION_MOMENTS}
          data={patterns?.FRICTION_MOMENTS}
          icon={<IconDangerTriangle />}
          iconSize={20}
          colors={{
            color: "var(--color-pattern-friction)",
            bg: "var(--color-pattern-friction-bg)",
          }}
        />
        <AnalysisCategory
          params={params}
          source={InsightCallSource.PATTERN}
          filter={filter}
          titleKey={TKey.CALL_ANALYSIS_PATTERN_EXCHANGE_TRENDS}
          subtitleKey={TKey.CALL_ANALYSIS_PATTERN_EXCHANGE_TRENDS_SUB}
          patternType={ProjectCallsPatternType.EXCHANGE_TRENDS}
          data={patterns?.EXCHANGE_TRENDS}
          icon={<IconTrendingUp />}
          iconSize={20}
          colors={{ color: "var(--color-pattern-trends)", bg: "var(--color-pattern-trends-bg)" }}
        />
      </div>

      {!UCallScope.isFiltered(filter) && (
        <div className={styles.rightColumn}>
          <AnalysisRecommendations params={params} kind={ProjectCallsRecommendationKind.CALL} />
        </div>
      )}
    </div>
  );
}
